<template>
    <div class="us-heat-map">
        <div v-if="loading" class="d-flex justify-content-center align-items-center" style="height: 500px;">
            <div class="spinner-border text-primary" role="status">
                <span class="visually-hidden">Loading map...</span>
            </div>
        </div>

        <div v-else-if="!stateData || stateData.length === 0" class="text-center text-muted py-5">
            <i class="fas fa-map fa-3x mb-3 opacity-25"></i>
            <p>No geographic distribution data available</p>
        </div>

        <div v-else class="map-container">
            <!-- Legend -->
            <div class="map-legend">
                <div class="legend-title">Client Distribution</div>
                <div class="legend-gradient">
                    <div class="gradient-bar" :style="{ background: gradientString }"></div>
                    <div class="legend-labels">
                        <span>{{ minClients }}</span>
                        <span>{{ maxClients }}</span>
                    </div>
                </div>
                <div class="legend-item">
                    <div class="legend-color no-data"></div>
                    <span>No clients</span>
                </div>
            </div>

            <!-- SVG Map -->
            <svg viewBox="0 0 975 610" preserveAspectRatio="xMidYMid meet" class="state-map">
                <g v-for="state in geojsonStates" :key="state.id">
                    <path
                        :d="state.path"
                        :fill="getStateColor(state.id)"
                        :stroke="hoveredState === state.id ? '#1F2937' : '#E5E7EB'"
                        stroke-width="1"
                        class="state"
                        :class="{ 'state-highlight': hoveredState === state.id }"
                        @mouseenter="showTooltip($event, state.id)"
                        @mousemove="updateTooltip($event)"
                        @mouseleave="hideTooltip"
                        @click="openStateLink($event, state.id)"
                    />
                    <text v-if="state.centroid" :x="state.centroid[0]" :y="state.centroid[1]" class="state-label" text-anchor="middle" pointer-events="none">
                        <tspan class="state-code">{{ state.id }}</tspan>
                        <tspan class="state-count" :x="state.centroid[0]" dy="12">({{ getClientCount(state.id) }})</tspan>
                    </text>
                </g>
            </svg>

            <!-- Tooltip -->
            <div v-if="tooltip.show" class="map-tooltip" :style="{ left: tooltip.x + 'px', top: tooltip.y + 'px' }">
                <div class="tooltip-header">{{ tooltip.stateName }}</div>
                <div class="tooltip-body">
                    <strong>{{ tooltip.clientCount.toLocaleString() }}</strong> client{{ tooltip.clientCount !== 1 ? 's' : '' }}
                    <span v-if="tooltip.percentage" class="text-muted ms-1">({{ tooltip.percentage }}%)</span>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import * as topojson from 'topojson-client'
import { geoPath, geoAlbersUsa, geoCentroid } from 'd3-geo'
import { scaleQuantize } from 'd3-scale'

const FIPS_MAP = {
    '01': 'AL', '02': 'AK', '04': 'AZ', '05': 'AR', '06': 'CA', '08': 'CO', '09': 'CT', '10': 'DE', '12': 'FL', '13': 'GA',
    '15': 'HI', '16': 'ID', '17': 'IL', '18': 'IN', '19': 'IA', '20': 'KS', '21': 'KY', '22': 'LA', '23': 'ME', '24': 'MD',
    '25': 'MA', '26': 'MI', '27': 'MN', '28': 'MS', '29': 'MO', '30': 'MT', '31': 'NE', '32': 'NV', '33': 'NH', '34': 'NJ',
    '35': 'NM', '36': 'NY', '37': 'NC', '38': 'ND', '39': 'OH', '40': 'OK', '41': 'OR', '42': 'PA', '44': 'RI', '45': 'SC',
    '46': 'SD', '47': 'TN', '48': 'TX', '49': 'UT', '50': 'VT', '51': 'VA', '53': 'WA', '54': 'WV', '55': 'WI', '56': 'WY'
}

const COLOR_PALETTES = {
    blue: ['#EFF6FF', '#DBEAFE', '#BFDBFE', '#93C5FD', '#60A5FA', '#3B82F6', '#2563EB', '#1D4ED8', '#1E40AF'],
    green: ['#F0FDF4', '#DCFCE7', '#BBF7D0', '#86EFAC', '#4ADE80', '#22C55E', '#16A34A', '#15803D', '#166534'],
    purple: ['#FAF5FF', '#F3E8FF', '#E9D5FF', '#D8B4FE', '#C084FC', '#A855F7', '#9333EA', '#7E22CE', '#6B21A8'],
    red: ['#FEF2F2', '#FEE2E2', '#FECACA', '#FCA5A5', '#F87171', '#EF4444', '#DC2626', '#B91C1C', '#991B1B'],
    orange: ['#FFF7ED', '#FFEDD5', '#FED7AA', '#FDBA74', '#FB923C', '#F97316', '#EA580C', '#C2410C', '#9A3412']
}

export default {
    name: 'USStateHeatMap',
    props: {
        stateData: { type: Array, default: () => [] },
        loading: { type: Boolean, default: false },
        colorScheme: { type: String, default: 'blue', validator: v => ['blue', 'green', 'purple', 'red', 'orange'].includes(v) },
        platformId: { type: [String, Number], required: true }
    },
    data() {
        return {
            geojsonStates: [],
            hoveredState: null,
            tooltip: { show: false, x: 0, y: 0, stateName: '', clientCount: 0, percentage: null }
        }
    },
    computed: {
        minClients() {
            const counts = this.stateData.map(s => s.client_count).filter(c => c > 0)
            return counts.length ? Math.min(...counts) : 0
        },
        maxClients() {
            return this.stateData.length ? Math.max(...this.stateData.map(s => s.client_count)) : 0
        },
        colorPalette() {
            return COLOR_PALETTES[this.colorScheme] || COLOR_PALETTES.blue
        },
        gradientString() {
            return `linear-gradient(to right, ${this.colorPalette.join(', ')})`
        }
    },
    watch: {
        stateData: { handler() { if (this.geojsonStates.length) this.renderMap() }, deep: true }
    },
    async mounted() {
        try {
            const response = await fetch('/data/us-states.json')
            const topology = await response.json()
            this.renderMap(topology)
        } catch (error) {
            console.error('Error loading US map:', error)
        }
    },
    methods: {
        renderMap(topology) {
            if (!topology) return

            const projection = geoAlbersUsa().scale(1300).translate([487.5, 305])
            const pathGenerator = geoPath().projection(projection)
            const states = topojson.feature(topology, topology.objects.states)

            this.geojsonStates = states.features.map(feature => ({
                id: FIPS_MAP[feature.id] || feature.id,
                path: pathGenerator(feature),
                centroid: projection(geoCentroid(feature))
            }))
        },

        getStateData(stateId) {
            return this.stateData.find(s => s.state_code === stateId)
        },

        getClientCount(stateId) {
            const state = this.getStateData(stateId)
            return state ? state.client_count : 0
        },

        getStateColor(stateId) {
            const state = this.getStateData(stateId)
            if (!state || state.client_count === 0) return '#F3F4F6'

            const colorScale = scaleQuantize().domain([this.minClients, this.maxClients]).range(this.colorPalette)
            return colorScale(state.client_count)
        },

        showTooltip(event, stateId) {
            const state = this.getStateData(stateId)
            const clientCount = state ? state.client_count : 0

            this.hoveredState = stateId
            this.tooltip = {
                show: true,
                x: event.clientX + 10,
                y: event.clientY - 10,
                stateName: state ? state.state_name : stateId,
                clientCount: clientCount,
                percentage: state?.percentage ? state.percentage.toFixed(1) : null
            }
        },

        updateTooltip(event) {
            if (this.tooltip.show) {
                this.tooltip.x = event.clientX + 10
                this.tooltip.y = event.clientY - 10
            }
        },

        hideTooltip() {
            this.hoveredState = null
            this.tooltip.show = false
        },

        openStateLink(event, stateId) {
            const url = this.$router.resolve({
                path: '/municipalities',
                query: { provider: this.platformId, state: stateId }
            }).href

            if (event.ctrlKey || event.metaKey || event.button === 1) {
                window.open(url, '_blank')
            } else {
                window.open(url, '_blank')
            }
        }
    }
}
</script>

<style scoped>
.us-heat-map { width: 100%; position: relative; }
.map-container { position: relative; width: 100%; background: #ffffff; border-radius: 8px; padding: 100px 20px 20px 20px; }
.state-map { width: 100%; height: auto; display: block; }
.state { cursor: pointer; transition: all 0.2s ease; }
.state:hover { filter: brightness(0.9); stroke-width: 2; }
.state-highlight { filter: brightness(0.85) !important; stroke-width: 2 !important; }
.state-label { font-size: 10px; font-weight: 600; fill: #374151; user-select: none; pointer-events: none; }
.state-code { font-size: 11px; font-weight: 700; }
.state-count { font-size: 9px; font-weight: 500; fill: #6B7280; }

.map-legend { position: absolute; top: -30px; right: 10px; background: white; border: 1px solid #E5E7EB; border-radius: 8px; padding: 12px; box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1); min-width: 160px; z-index: 10; }
.legend-title { font-size: 13px; font-weight: 600; color: #374151; margin-bottom: 12px; }
.legend-gradient { margin-bottom: 12px; }
.gradient-bar { height: 20px; border-radius: 4px; margin-bottom: 4px; border: 1px solid #E5E7EB; }
.legend-labels { display: flex; justify-content: space-between; font-size: 11px; color: #6B7280; }
.legend-item { display: flex; align-items: center; gap: 8px; font-size: 12px; color: #6B7280; padding-top: 8px; border-top: 1px solid #E5E7EB; }
.legend-color { width: 20px; height: 20px; border-radius: 4px; border: 1px solid #E5E7EB; }
.legend-color.no-data { background: #F3F4F6; }

.map-tooltip { position: fixed; background: rgba(17, 24, 39, 0.95); color: white; padding: 10px 14px; border-radius: 6px; font-size: 13px; pointer-events: none; z-index: 1000; box-shadow: 0 4px 6px rgba(0, 0, 0, 0.2); max-width: 200px; }
.tooltip-header { font-weight: 600; margin-bottom: 4px; font-size: 14px; }
.tooltip-body { font-size: 12px; color: #E5E7EB; }

@media (max-width: 768px) {
    .map-legend { position: static; margin-bottom: 20px; width: 100%; }
    .map-container { padding: 15px; }
}
</style>
